/* eslint-disable react/style-prop-object */
import React, { useState } from "react";
import { Invitation } from "../../../domain/Invitation";
import { useInvitations } from "../../../hooks/useInvitations/useInvitations";
import { useUserContext } from "../../../providers/UserProvider";
import { ATTRIBUTE_ROLES, CONTEXT_SCOPES } from "../../../constants/constants";
import styles from "../../pages/Invitations/Invitations.module.scss";
import { Button, Dropdown } from "@optimizely/axiom";
import { Sidebar } from "../Sidebar/Sidebar";
import { AccessManagementInvitationForm } from "../Access/AccessManagementInvitationForm";
import { useParams, useLocation } from "react-router-dom";
import { InvitationFormWithSteps } from "../Access/InvitationFormWithSteps/InvitationFormWithSteps";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";
import { CsvUploadSheet } from "../CsvUploadSheet/CsvUploadSheet";
import { BulkUploadWithSteps } from "../BulkUploadWithSteps/BulkUploadWithSteps";

export const InviteUserButtonAndSidebar = () => {
    const { organizationId, accessContext, canUserDoAction } = useUserContext();
    const [showInvitationForm, setShowInvitationForm] = useState(false);
    const [showCsvUpload, setShowCsvUpload] = useState(false);
    const { productId, instanceId, projectId } = useParams();
    const { pathname } = useLocation();
    const { enabled: invitationStepsEnabled } = useFeatureFlag(Flags.ENABLE_INVITATION_STEPS);
    const { enabled: csvUploadEnabled } = useFeatureFlag(Flags.ENABLE_BULK_UPLOAD);
    const { enabled: bulkUploadAccessEnabled } = useFeatureFlag(Flags.ENABLE_BULK_UPLOAD_ACCESS);

    const { createInvitation } = useInvitations({
        organizationId: organizationId
    });

    const handleFormCancel = () => {
        setShowInvitationForm(false);
    };
    const handleCreateInvitation = async ({
        newInvitation,
        inviteAnother
    }: {
        newInvitation: Invitation | null;
        inviteAnother: boolean;
    }) => {
        if (!newInvitation) {
            return false;
        }

        await createInvitation(newInvitation);
        if (!inviteAnother) {
            setShowInvitationForm(false);
        }
        return true;
    };

    const canAccessInvite =
        canUserDoAction({
            action: ATTRIBUTE_ROLES.INVITATIONS.CREATE,
            context: { AttributeId: projectId }
        }) ||
        canUserDoAction({
            action: ATTRIBUTE_ROLES.INVITATIONS.CREATE,
            context: { InstanceId: instanceId }
        }) ||
        canUserDoAction({
            action: ATTRIBUTE_ROLES.INVITATIONS.CREATE,
            context: { ProductId: productId }
        });

    const usersPage = pathname === "/user-management/users" || pathname === "/user-management/users/";
    const invitationsPage = pathname === "/user-management/invitations" || pathname === "/user-management/invitations/";

    const usersFlow = usersPage || invitationsPage;

    const showInviteUser =
        canAccessInvite && !!organizationId && (usersFlow || accessContext?.scope !== CONTEXT_SCOPES.ORGANIZATION);

    return (
        <>
            {showInviteUser && (
                <>
                    <div className={styles["invitations__add-invitation"]}>
                        {csvUploadEnabled ? (
                            <Dropdown
                                buttonContent="Invite User..."
                                style="highlight"
                                arrowIcon="down"
                                data-testid="invite-user-dropdown"
                            >
                                <Dropdown.Contents>
                                    <Dropdown.ListItem>
                                        {/* testSection, not data-testid: BlockLink destructures a
                                            fixed prop list, so anything else is dropped before it
                                            renders. This one emits data-test-section. */}
                                        <Dropdown.BlockLink
                                            onClick={() => setShowInvitationForm(true)}
                                            testSection="invite-users-manually"
                                        >
                                            <Dropdown.BlockLinkText text="Invite Users Manually" />
                                        </Dropdown.BlockLink>
                                    </Dropdown.ListItem>
                                    <Dropdown.ListItem>
                                        <Dropdown.BlockLink
                                            onClick={() => setShowCsvUpload(true)}
                                            testSection="import-users-csv"
                                        >
                                            <Dropdown.BlockLinkText text="Import Users with CSV" />
                                        </Dropdown.BlockLink>
                                    </Dropdown.ListItem>
                                </Dropdown.Contents>
                            </Dropdown>
                        ) : (
                            <Button style="highlight" onClick={() => setShowInvitationForm(true)}>
                                Invite User...
                            </Button>
                        )}
                    </div>
                    <Sidebar heading="Invite User" isOpen={showInvitationForm} onCancel={handleFormCancel} noTransform>
                        {invitationStepsEnabled ? (
                            <InvitationFormWithSteps onCreate={handleCreateInvitation} usersFlow={usersFlow} />
                        ) : (
                            <AccessManagementInvitationForm
                                onCancel={handleFormCancel}
                                onCreate={handleCreateInvitation}
                                usersFlow={usersFlow}
                            />
                        )}
                    </Sidebar>
                    {bulkUploadAccessEnabled ? (
                        <BulkUploadWithSteps
                            isOpen={showCsvUpload}
                            onClose={() => setShowCsvUpload(false)}
                            organizationId={organizationId}
                        />
                    ) : (
                        <CsvUploadSheet
                            isOpen={showCsvUpload}
                            onClose={() => setShowCsvUpload(false)}
                            organizationId={organizationId}
                        />
                    )}
                </>
            )}
        </>
    );
};
